import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useCreateListing } from '@/contexts/CreateListingContext';
import { CinematicVideoDemo } from '@/components/CinematicVideoDemo';
import { Reveal } from '@/components/Reveal';
import {
  Clapperboard,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Zap,
  Camera,
  Video,
  Wand2,
  ChevronRight,
  Star,
  Globe,
  Clock,
  DollarSign,
} from 'lucide-react';

const cinematicMoves = [
  { name: 'Dolly In', description: 'Camera glides toward the subject, pulling the viewer into the scene.' },
  { name: 'Cinematic Pan', description: 'Smooth horizontal sweep across the full width of the room.' },
  { name: 'Push In', description: 'Slow, deliberate zoom into the hero feature of the space.' },
  { name: 'Pull Back Reveal', description: 'Camera retreats to gradually reveal the whole room.' },
  { name: 'Tilt Up', description: 'Dramatic upward tilt — ideal for high ceilings and exteriors.' },
  { name: 'Drift', description: 'Gentle sideways float that gives a relaxed, lifestyle feel.' },
  { name: 'Float', description: 'Weightless vertical rise that shows off the full height of a space.' },
  { name: 'Orbit', description: 'Slow 360-degree arc around the focal point of the room.' },
];

const steps = [
  {
    number: '01',
    icon: Camera,
    title: 'Upload your photos',
    description: 'Add up to 8 property photos. ListBright automatically sorts them — exterior first, then room by room.',
  },
  {
    number: '02',
    icon: Wand2,
    title: 'AI generates each clip',
    description: 'Every photo is sent to a state-of-the-art video AI model. It renders 5 seconds of genuine cinematic motion per shot.',
  },
  {
    number: '03',
    icon: Video,
    title: 'Clips are stitched into one video',
    description: 'All clips are assembled into a single polished HD video — ready to download and share in minutes.',
  },
];

export default function CinematicVideo() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { triggerCreate } = useCreateListing();

  const handleCta = () => {
    if (user) {
      triggerCreate();
      navigate('/');
    } else {
      navigate('/auth?next=create');
    }
  };

  return (
    <Layout>
      <PageMeta
        title="Cinematic AI Video – ListBright"
        description="Turn property photos into genuine AI-generated cinematic video clips. Dolly, pan, orbit, float — real AI motion on every shot. $5 per listing."
        breadcrumbs={[
          { label: 'Features', path: '/features' },
          { label: 'Cinematic AI Video', path: '/cinematic-video' },
        ]}
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background via-primary/5 to-accent/10 py-20 lg:py-28">
        {/* Background grid */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        {/* Glow blobs */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-6xl mx-auto px-4 relative">
          <Link
            to="/features"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ChevronRight className="w-4 h-4 rotate-180" /> All features
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Clapperboard className="w-6 h-6 text-primary" />
                </div>
                <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                  Premium Add-on
                </Badge>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.05]">
                Cinematic AI{' '}
                <span className="text-gradient">Video</span>
              </h1>

              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
                Not a slideshow. Not a template. Real AI-generated motion on every photo — dolly-ins, pans, orbits — stitched into one polished HD video that makes buyers feel like they're already inside the property.
              </p>

              <ul className="space-y-2.5">
                {[
                  'Genuine AI motion per photo — not a zoom filter',
                  '8 cinematic camera moves, rotated intelligently',
                  'Up to 8 photos → one seamless HD video',
                  'Auto-sorted room order for a natural tour flow',
                  'Download and share in minutes',
                ].map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-sm text-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button variant="hero" size="lg" className="text-base px-7 py-6 shadow-lg" onClick={handleCta}>
                  <Zap className="w-5 h-5 mr-2" />
                  {user ? 'Create My Video' : 'Get Started — $5'}
                </Button>
                <Button variant="outline" size="lg" className="text-base px-6 py-6" asChild>
                  <Link to="/pricing">See all pricing</Link>
                </Button>
              </div>

              <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5 text-primary" /> $5 flat per listing</span>
                <span className="text-border">·</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-primary" /> Ready in minutes</span>
                <span className="text-border">·</span>
                <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5 text-primary" /> Share anywhere</span>
              </div>
            </div>

            {/* Right — live interactive demo */}
            <div className="relative">
              <CinematicVideoDemo onGetStarted={handleCta} />
              {/* Decorative glow */}
              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-20 lg:py-24 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="outline" className="mb-4">
              <Zap className="w-3.5 h-3.5 mr-1.5 text-primary" /> How it works
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
              From photos to cinematic video in minutes
            </h2>
            <p className="text-muted-foreground">
              No editing software, no camera crew, no technical knowledge required.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <Reveal key={step.number} delay={i * 100}>
                  <div className="card-premium relative h-full p-7 rounded-2xl bg-card border border-border/60 space-y-4">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-primary/50 tracking-widest">{step.number}</span>
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-foreground">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Camera moves ── */}
      <section className="py-20 lg:py-24 bg-card/50 border-b border-border/40">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="outline" className="mb-4">
              <Clapperboard className="w-3.5 h-3.5 mr-1.5 text-primary" /> 8 cinematic moves
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
              Every shot, a different camera move
            </h2>
            <p className="text-muted-foreground">
              The AI rotates through 8 professional cinematography techniques so your video feels directed — not repetitive.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {cinematicMoves.map((move, i) => (
              <Reveal key={move.name} delay={(i % 4) * 60}>
                <div
                  className="group h-full p-5 rounded-xl bg-background border border-border/60 hover:border-primary/30 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 space-y-2"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                      <span className="text-[10px] font-bold text-primary">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <p className="font-semibold text-sm text-foreground">{move.name}</p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{move.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why it matters ── */}
      <section className="py-20 lg:py-24 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge variant="outline" className="mb-2">
                <Star className="w-3.5 h-3.5 mr-1.5 text-primary" /> Why it matters
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-display font-bold leading-tight">
                Video listings get more eyes.<br />
                <span className="text-gradient">More eyes get more offers.</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Buyers scroll past hundreds of listings. A video that moves makes them stop. Cinematic footage builds the emotional connection that static photos and basic slideshows simply can't create.
              </p>
              <ul className="space-y-4">
                {[
                  { stat: 'More enquiries', detail: 'Listings with video consistently attract more contact from serious buyers than photo-only listings.' },
                  { stat: 'Faster sales', detail: 'Properties with professional video marketing spend less time on market — every day saved counts.' },
                  { stat: 'Higher asking price', detail: 'Premium presentation supports a premium price. Buyers pay more for homes that feel aspirational.' },
                ].map((item) => (
                  <li key={item.stat} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground text-sm">{item.stat}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Stats card */}
            <div className="space-y-4">
              {[
                { value: '8', unit: 'cinematic clips', label: 'One per photo, unique camera move on each' },
                { value: '5s', unit: 'per clip', label: 'True AI-generated motion, not a zoom effect' },
                { value: '$5', unit: 'flat', label: 'One-time add-on per listing — no subscription' },
                { value: '~10', unit: 'min', label: 'From upload to downloadable HD video' },
              ].map((item, i) => (
                <Reveal key={item.unit} delay={i * 80} className="card-premium flex items-center gap-4 p-5 rounded-xl bg-card border border-border/60">
                  <div className="shrink-0 text-right w-20">
                    <span className="text-3xl font-display font-bold text-primary">{item.value}</span>
                    <span className="text-sm font-semibold text-foreground ml-1">{item.unit}</span>
                  </div>
                  <div className="w-px h-10 bg-border shrink-0" />
                  <p className="text-sm text-muted-foreground">{item.label}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing callout ── */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-primary/5 via-card/40 to-accent/5">
        <div className="container max-w-3xl mx-auto px-4 text-center space-y-6">
          <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
            <DollarSign className="w-3.5 h-3.5 mr-1.5" /> Simple pricing
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-display font-bold">
            One video. One flat price.
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Add Cinematic AI Video to any listing for <strong className="text-foreground">$5</strong>. No subscription, no hidden fees. Pay once per listing and keep the video forever.
          </p>

          <div className="inline-block bg-card rounded-2xl border border-border/60 shadow-elevated p-8 text-left max-w-sm mx-auto">
            <div className="flex items-end gap-1 mb-1">
              <span className="text-5xl font-display font-bold text-foreground">$5</span>
              <span className="text-muted-foreground mb-2">/ listing</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">One-time · no subscription</p>
            <ul className="space-y-2.5 mb-6">
              {[
                'Up to 8 AI-generated cinematic clips',
                'One stitched HD video download',
                'All 8 camera move styles included',
                'Unlimited reshares',
              ].map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Button variant="hero" className="w-full" size="lg" onClick={handleCta}>
              <Sparkles className="w-4 h-4 mr-2" />
              {user ? 'Create a Listing' : 'Get Started'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="py-16 border-t border-border/40">
        <div className="container max-w-4xl mx-auto px-4 text-center space-y-4">
          <h2 className="text-2xl sm:text-3xl font-display font-bold">Ready to make your listing move?</h2>
          <p className="text-muted-foreground">Create your listing, add the Cinematic Video add-on, and stand out on every portal.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button variant="hero" size="lg" className="text-base px-8 py-6 shadow-lg" onClick={handleCta}>
              <Zap className="w-5 h-5 mr-2" />
              {user ? 'Create a Listing' : 'Get Started Free'}
            </Button>
            <Button variant="outline" size="lg" className="text-base px-6 py-6" asChild>
              <Link to="/features">Explore all features <ChevronRight className="w-4 h-4 ml-1" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
